// src/stores/userStore.js
import { defineStore } from 'pinia';
import { ref, computed, watch, reactive } from 'vue';
import { auth, db, firebaseApp } from '@/firebaseConfig'; 
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  onSnapshot, 
  where, 
  deleteDoc,
  writeBatch,
  getDocs,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { translateSentinelError } from '@/utils/sentinelErrorHandler';

/**
 * 🛡️ PHASE 18: FUNCTIONAL AUTHORITY & SELF-HEALING REGISTRY
 * Update 18.12: Moved computed properties to top-level scope to satisfy ESLint compilation targets.
 * Decoupled Client Access listeners from MFA state to resolve lockout bypasses.
 * Removed top-level router dependency to resolve circular import deadlocks.
 * Mandate: Zero-Truncation / 100% Fidelity.
 */
const LOCAL_APP_VERSION = 18.12;

export const useUserStore = defineStore('user', () => {
  // --- STATE: AUTHENTICATION & IDENTITY ---
  const userProfile = ref(null);
  const clientProfile = ref(null);
  const claims = ref(null);
  const isAuthReady = ref(false);
  const isUserProfileLoading = ref(true);
  const mfaVerified = ref(false);

  // 🛡️ STATE: GLOBAL LOCKOUT PROTOCOLS
  const isMaintenanceMode = ref(false);

  // --- STATE: SYSTEM NOTIFICATIONS & ACTIONS ---
  const rawNotifications = ref([]); 
  const pendingToolboxTalksDetails = ref([]);
  const globalPendingActionCount = ref(0); 
  const dismissedAlertIds = ref([]); 
  
  // 🛡️ REAL-TIME CUSTOM ROLE PERMISSIONS STATE
  const rolePermissions = ref({});

  // --- COMPUTED: DERIVED STATES & ROLES ---
  const isLoggedIn = computed(() => !!userProfile.value);
  const role = computed(() => String(claims.value?.role || userProfile.value?.role || 'standard').toLowerCase().trim());
  const isAdmin = computed(() => role.value === 'admin' || role.value === 'super-admin');

  const isClientClosed = computed(() => {
    if (!clientProfile.value) return false;
    return clientProfile.value.isAccessEnabled === false || 
           ['closed', 'suspended', 'inactive'].includes(String(clientProfile.value.status || '').toLowerCase().trim());
  });

  const isManager = computed(() => userProfile.value?.isDepartmentManager === true);
  const isDirector = computed(() => userProfile.value?.isDepartmentDirector === true);
  
  const isOnLeave = computed(() => userProfile.value?.isOnAnnualLeave === true);
  const isMfaEnrolled = computed(() => !!auth.currentUser?.multiFactor?.enrolledFactors?.length);
  const signatureSeal = computed(() => userProfile.value?.signatureDataUrl || userProfile.value?.signatureUrl || null);
  
  const isHSManager = computed(() => {
    const title = String(userProfile.value?.jobTitle || '').toLowerCase();
    return title.includes('safety') && title.includes('manager');
  });
  
  // 🔄 Self-healing Live Permissions (combines live Firestore custom roles, profile settings, and custom claims)
  const permissions = computed(() => {
    // 🛡️ Live database custom role permissions take top priority over cached token claims!
    const livePermissions = {
      ...(claims.value?.permissions || {}),
      ...(userProfile.value?.permissions || {}),
      ...(rolePermissions.value || {})
    };

    if (isAdmin.value) {
      return { 
        canViewUsers: true, 
        canViewOnboarding: true, 
        canViewHealthAndSafety: true, 
        canViewTraining: true, 
        canViewEngineering: true, 
        canViewVehicles: true, 
        canViewFinance: true, 
        canViewAnalytics: true,
        canViewFatigue: true,
        canManagePoliciesAndProcedures: true,
        canViewResources: true,
        canViewTasks: true,
        ...livePermissions 
      };
    }
    return livePermissions;
  });

  const hasAdminAccess = computed(() => {
    return isAdmin.value || 
           isManager.value || 
           isDirector.value || 
           isHSManager.value || 
           permissions.value?.canViewUsers === true ||
           permissions.value?.canManageUsers === true;
  });

  const clientId = computed(() => claims.value?.clientId || userProfile.value?.clientId);

  const unreadNotificationCount = computed(() => {
    const notifications = rawNotifications.value || [];
    const dbCount = notifications.filter(n => !dismissedAlertIds.value.includes(n.id) && (n.type !== 'ACTION_COMPLETED' || hasAdminAccess.value)).length;
    return dbCount + globalPendingActionCount.value;
  });

  const pendingTalks = computed(() => {
    const userId = auth.currentUser?.uid || userProfile.value?.userId;
    if (!userId) return [];
    return pendingToolboxTalksDetails.value.filter(talk => {
      const entry = talk.attendees?.find(a => a.userId === userId);
      return entry && (entry.status === 'Pending' || entry.status === 'Needs Attention');
    });
  });
  
  let unsubscribeNotifications = null;
  let unsubscribeDismissals = null;
  let unsubscribeSystem = null; 
  let unsubscribeClient = null; 
  let unsubscribeRole = null;
  const backgroundUnsubs = [];

  // --- ACTIONS: IDENTITY & PASSWORD HANDSHAKES (SENTINEL SIGNAL MASKED) ---

  /**
   * 🛡️ TRIGGER SMS MFA HANDSHAKE
   */
  const triggerSmsMfaHandshake = async (email, session) => {
    const functions = getFunctions(firebaseApp, 'europe-west2'); 
    const dispatchSms = httpsCallable(functions, 'dispatchSmsMfaCode');
    const normalizedEmail = String(email).toLowerCase().trim();
    
    try {
      const result = await dispatchSms({ email: normalizedEmail, session });
      return result.data.success;
    } catch (error) {
      console.error("SMS Handshake Failure:", error);
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  /**
   * 🛡️ VERIFY SMS MFA HANDSHAKE
   */
  const verifySmsMfaHandshake = async (email, session, code) => {
    const functions = getFunctions(firebaseApp, 'europe-west2');
    const verifySms = httpsCallable(functions, 'verifySmsMfaCode');
    const normalizedEmail = String(email).toLowerCase().trim();

    try {
      const result = await verifySms({ email: normalizedEmail, session, code });
      if (result.data.success && result.data.customToken) {
        const userCredential = await signInWithCustomToken(auth, result.data.customToken);
        mfaVerified.value = true;
        return userCredential.user;
      }
      throw new Error("Identity verification failed.");
    } catch (error) {
      console.error("Verification Logic Failure:", error);
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  /**
   * 🛡️ DISPATCH PASSWORD RESET SMS
   */
  const dispatchPasswordResetSms = async (email) => {
    const functions = getFunctions(firebaseApp, 'europe-west2');
    const dispatch = httpsCallable(functions, 'dispatchPasswordResetSms');
    const normalizedEmail = String(email).toLowerCase().trim();

    try {
      const result = await dispatch({ email: normalizedEmail });
      
      // 🛡️ DYNAMIC HANDSHAKE EXCEPTION SCALER:
      // If the backend function returned a successful API execution but failed to send/locate,
      // we must explicitly throw the returned payload message to notify the user.
      if (!result.data || result.data.success === false) {
        throw new Error(result.data?.message || "Operational Handshake Failure: Secure recovery token could not be dispatched.");
      }
      
      return result.data.success;
    } catch (error) {
      console.error("Reset Dispatch failure:", error);
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  /**
   * 🛡️ DISPATCH PASSWORD RESET HANDSHAKE (Alias routing to dispatchPasswordResetSms)
   */
  const dispatchPasswordResetHandshake = async (email) => {
    return await dispatchPasswordResetSms(email);
  };

  /**
   * 🛡️ COMPLETE PASSWORD RESET HANDSHAKE
   */
  const completePasswordResetHandshake = async (email, code, newPassword) => {
    const functions = getFunctions(firebaseApp, 'europe-west2');
    const reset = httpsCallable(functions, 'verifyAndCompletePasswordReset');
    const normalizedEmail = String(email).toLowerCase().trim();

    try {
      const result = await reset({ email: normalizedEmail, code, newPassword });
      return result.data.success;
    } catch (error) {
      console.error("Key Completion failure:", error);
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  /**
   * 🛡️ EMAIL FALLBACK PROTOCOL
   */
  const triggerEmailMfaFallback = async (email, session) => {
    const functions = getFunctions(firebaseApp, 'europe-west2');
    const dispatchEmailMfa = httpsCallable(functions, 'dispatchEmailMfaCode');
    const normalizedEmail = String(email).toLowerCase().trim();

    try {
      const result = await dispatchEmailMfa({ email: normalizedEmail, session });
      
      if (!result.data || result.data.success === false) {
        throw new Error(result.data?.message || "Operational Fallback Failure: Email code could not be dispatched.");
      }
      
      return result.data.success;
    } catch (error) {
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  // --- ACTIONS: REGISTRY MANAGEMENT ---

  /**
   * 🛡️ UPDATE ABSENCE STATUS
   */
  const updateAbsenceStatus = async (status) => {
    if (!userProfile.value?.userId) return;
    try {
      const userRef = doc(db, 'userProfiles', userProfile.value.userId);
      await updateDoc(userRef, { isOnAnnualLeave: status });
      userProfile.value.isOnAnnualLeave = status;
    } catch (error) {
      console.error("Registry Sync Failure:", error);
      // 🛡️ Sentinel Signal Applied
      throw new Error(translateSentinelError(error));
    }
  };

  /**
   * 🛡️ CLEAR USER DATA
   */
  const clearUserData = () => {
    userProfile.value = null; 
    clientProfile.value = null; 
    claims.value = null;
    rawNotifications.value = []; 
    pendingToolboxTalksDetails.value = [];
    dismissedAlertIds.value = []; 
    globalPendingActionCount.value = 0;
    mfaVerified.value = false; 
    isMaintenanceMode.value = false;
    rolePermissions.value = {};
    
    if (unsubscribeNotifications) unsubscribeNotifications();
    if (unsubscribeDismissals) unsubscribeDismissals();
    if (unsubscribeSystem) { unsubscribeSystem(); unsubscribeSystem = null; }
    if (unsubscribeClient) { unsubscribeClient(); unsubscribeClient = null; }
    if (unsubscribeRole) { unsubscribeRole(); unsubscribeRole = null; }
    
    backgroundUnsubs.forEach(u => u()); 
    backgroundUnsubs.length = 0;
  };

  const setGlobalPendingActionCount = (count) => {
    globalPendingActionCount.value = count;
  };

  const dismissAlert = async (alertId) => {
    const user = auth.currentUser;
    if (!user || !alertId) return;
    try {
      const dismissalRef = doc(db, 'userProfiles', user.uid, 'dismissed_alerts', alertId);
      await setDoc(dismissalRef, { dismissedAt: new Date().toISOString(), alertId });
    } catch (e) { console.warn("Dismissal silent fail:", e); }
  };

  /**
   * 🛡️ INITIALISE BACKGROUND REGISTRY
   */
  const initBackgroundRegistry = (clientIdVal, userId) => {
    if (!userId || !clientIdVal || !mfaVerified.value) return;
    
    backgroundUnsubs.forEach(u => u());
    backgroundUnsubs.length = 0;

    const dismissalRef = collection(db, 'userProfiles', userId, 'dismissed_alerts');
    unsubscribeDismissals = onSnapshot(dismissalRef, (snap) => {
      dismissedAlertIds.value = snap.docs.map(d => d.id);
    });

    const counts = reactive({ tasks: 0, support: 0, vehicles: 0, training: 0 });
    const updateTotal = () => {
      globalPendingActionCount.value = counts.tasks + counts.support + counts.vehicles + counts.training;
    };

    backgroundUnsubs.push(onSnapshot(query(
      collection(db, 'user_actions'), 
      where('targetUserId', '==', userId), 
      where('status', '==', 'Pending')
    ), (snap) => {
      const activeTasks = snap.docs.filter(d => !dismissedAlertIds.value.includes(d.id));
      counts.tasks = activeTasks.length;
      updateTotal();
    }));

    const canSeeEng = permissions.value?.canViewEngineering;
    const currentRole = String(role.value).toLowerCase().trim();
    
    if (currentRole === 'super-admin' || canSeeEng) {
      backgroundUnsubs.push(onSnapshot(query(
        collection(db, 'supportRequestSubmissions'), 
        where('clientId', '==', clientIdVal), 
        where('status', '==', 'Pending')
      ), (snap) => {
        const active = snap.docs.filter(d => !dismissedAlertIds.value.includes(d.id));
        counts.support = active.length;
        updateTotal();
      }));
    }

    if (isAdmin.value || permissions.value?.canViewVehicles) {
      backgroundUnsubs.push(onSnapshot(query(collection(db, 'clients', clientIdVal, 'vehicles')), (snap) => {
        const thirtyDays = new Date(Date.now() + (30 * 24 * 60 * 60 * 1000));
        let alertCount = 0;
        snap.forEach(d => {
          if (!dismissedAlertIds.value.includes(d.id)) {
            const vData = d.data();
            const mot = vData.motExpiry ? new Date(vData.motExpiry) : null;
            if (mot && mot < thirtyDays) alertCount++;
          }
        });
        counts.vehicles = alertCount;
        updateTotal();
      }));
    }

    if (!isAdmin.value) {
      watch(() => pendingTalks.value.length, (newVal) => {
        counts.training = newVal || 0;
        updateTotal();
      }, { immediate: true });
    }
  };

  /**
   * 🛡️ FETCH USER & SYNC IDENTITY
   */
  const fetchUser = async (user) => {
    if (!user) {
      clearUserData();
      isUserProfileLoading.value = false;
      isAuthReady.value = true;
      return;
    }
    
    isUserProfileLoading.value = true;
    try {
      if (!unsubscribeSystem) {
        unsubscribeSystem = onSnapshot(doc(db, 'systemSettings', 'global'), (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            isMaintenanceMode.value = data.maintenanceMode === true;
            if (data.appVersion && data.appVersion > LOCAL_APP_VERSION) window.location.reload();
          }
        });
      }

      const idTokenResult = await user.getIdTokenResult(true);
      claims.value = idTokenResult.claims;
      mfaVerified.value = !!user.multiFactor?.enrolledFactors?.length || claims.value?.mfa_verified === true;

      const userProfileRef = doc(db, 'userProfiles', user.uid);
      const docSnap = await getDoc(userProfileRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();

        /** 🛡️ SELF-HEALING DEPARTMENT PROTOCOL 
         * Logic to recover missing department strings from multi-tenancy arrays.
        */
        if (!data.department && Array.isArray(data.departments) && data.departments.length > 0) {
            data.department = data.departments[0];
        }

        userProfile.value = { ...data, userId: user.uid };
      }

      const cid = claims.value?.clientId || userProfile.value?.clientId;
      
      // 🛡️ UNCOUPLED CLIENT LOCKOUT STATUS LISTENER
      // Subscribes immediately upon resolving a clientId, independent of MFA enrollment status.
      if (cid) {
        if (unsubscribeClient) unsubscribeClient();
        unsubscribeClient = onSnapshot(doc(db, 'clients', cid), (snap) => {
          if (snap.exists()) {
            clientProfile.value = { ...snap.data(), id: snap.id };
          }
        });
      }

      if (cid && mfaVerified.value) {
        initBackgroundRegistry(cid, user.uid);

        const notificationsRef = collection(db, 'userProfiles', user.uid, 'notifications');
        unsubscribeNotifications = onSnapshot(query(notificationsRef, where('isRead', '==', false)), 
          (snapshot) => { 
            rawNotifications.value = snapshot.docs.map(d => ({ id: d.id, ...d.data() })); 
          },
          () => { 
            rawNotifications.value = []; 
          }
        );
      }
    } catch (error) {
      console.warn("Handshake Active:", error.message);
    } finally {
      isUserProfileLoading.value = false;
      isAuthReady.value = true; 
    }
  };
  
  const removeNotification = async (notificationId) => {
    const user = auth.currentUser;
    if (!user || !notificationId) return;
    try {
      await deleteDoc(doc(db, 'userProfiles', user.uid, 'notifications', notificationId));
      await dismissAlert(notificationId); 
    } catch (error) { console.warn("Remove notify fail:", error); }
  };

  const deleteAllNotifications = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const batch = writeBatch(db);
      const notificationsRef = collection(db, 'userProfiles', user.uid, 'notifications');
      const snap = await getDocs(query(notificationsRef, where('isRead', '==', false)));
      snap.forEach(d => batch.delete(d.ref));
      await batch.commit();
    } catch (error) { console.warn("Delete all fail:", error); }
  };

  const purgeNotificationByRelatedId = async (relatedId) => {
    const user = auth.currentUser;
    if (!user || !relatedId) return;
    try {
      const notificationsRef = collection(db, 'userProfiles', user.uid, 'notifications');
      const snap = await getDocs(query(notificationsRef, where('relatedId', '==', relatedId)));
      const batch = writeBatch(db);
      snap.forEach(d => batch.delete(d.ref));
      await batch.commit();
    } catch (error) { console.warn("Purge fail:", error); }
  };
  
  watch(rawNotifications, async (newVal) => {
    if (!Array.isArray(newVal) || newVal.length === 0 || !mfaVerified.value) {
      pendingToolboxTalksDetails.value = [];
      return;
    }
    const talkInvites = newVal.filter(n => n.type === 'TOOLBOX_TALK_INVITE');
    const talkIdsToFetch = [...new Set(talkInvites.map(n => n.relatedId))];
    if (talkIdsToFetch.length === 0) {
      pendingToolboxTalksDetails.value = [];
      return;
    }
    const fetchedTalks = [];
    for (const id of talkIdsToFetch) {
        try {
            const talkSnap = await getDoc(doc(db, 'toolboxTalks', id));
            if (talkSnap.exists()) fetchedTalks.push({ id: talkSnap.id, ...talkSnap.data() });
        } catch (e) { console.warn("Fetch talk fail:", e); }
    }
    pendingToolboxTalksDetails.value = fetchedTalks;
  });

  /** 🛡️ MISSION CRITICAL WATCHER
   * Correctly monitors for System Lockout conditions without circular import deadlocks.
   * Dynamically imports the router on execution.
   */
  watch([isMaintenanceMode, isClientClosed], async ([maintenance, closed]) => {
    if (role.value === 'super-admin') return;
    if (maintenance || closed) {
      try {
        const routerModule = await import('@/router');
        const activeRouter = routerModule.default;
        const name = activeRouter.currentRoute.value.name;
        
        if (name && !['SystemLockout', 'login', 'PublicObservationForm', 'PublicAccidentForm', 'PublicWitnessStatement', 'PublicHealthSafetyFormsList'].includes(name)) {
          activeRouter.push({ name: 'SystemLockout', query: { reason: maintenance ? 'maintenance' : 'client_closed' } });
        }
      } catch (err) {
        console.error("Router resolution failure in lockout watcher:", err);
      }
    }
  }, { immediate: true });

  // 👁️ DYNAMIC WORKSPACE HOT-SWAP REGISTRY LISTENER & REAL-TIME ROLE SYNC
  // Watches the computed clientId. The moment it changes during impersonation,
  // we securely re-initialize the client lockout listener, background registers, and notification streams.
  watch(clientId, (newCid) => {
    const userId = auth.currentUser?.uid || userProfile.value?.userId;
    if (newCid && userId && mfaVerified.value) {
      // 1. Re-initialize background counters (tasks, pending audits, vehicles)
      initBackgroundRegistry(newCid, userId);

      // 2. Re-initialize Client Lockout Profile Listener
      if (unsubscribeClient) unsubscribeClient();
      unsubscribeClient = onSnapshot(doc(db, 'clients', newCid), (snap) => {
        if (snap.exists()) {
          clientProfile.value = { ...snap.data(), id: snap.id };
        }
      });

      // 3. Re-initialize Notifications Stream
      if (unsubscribeNotifications) unsubscribeNotifications();
      const notificationsRef = collection(db, 'userProfiles', userId, 'notifications');
      unsubscribeNotifications = onSnapshot(query(notificationsRef, where('isRead', '==', false)), 
        (snapshot) => { 
          rawNotifications.value = snapshot.docs.map(d => ({ id: d.id, ...d.data() })); 
        },
        () => { 
          rawNotifications.value = []; 
        }
      );
    }
  });

  // Real-time custom role permissions sync (target path mapped to permissionRoles)
  watch([clientId, () => userProfile.value?.permissionRoleId, role], (newValues) => {
    const [cid, permissionRoleId, activeRole] = newValues;
    console.log("🛡️ Sentinel Safe Diagnostics - watcher triggered:", { cid, permissionRoleId, activeRole });
    
    if (unsubscribeRole) {
      unsubscribeRole();
      unsubscribeRole = null;
    }
    rolePermissions.value = {};

    // 🛡️ Unconditionally resolve target custom role ID if permissionRoleId exists (handles alternate manager title mappings)
    const targetRole = permissionRoleId || activeRole;
    console.log("🛡️ Sentinel Safe Diagnostics - resolved targetRole:", targetRole);

    if (!cid || !targetRole || targetRole === 'admin' || targetRole === 'super-admin') return;

    const roleRef = doc(db, 'clients', cid, 'permissionRoles', targetRole);
    unsubscribeRole = onSnapshot(roleRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        console.log("🛡️ Sentinel Safe Diagnostics - role document located:", data);
        rolePermissions.value = data.permissions || {};
      } else {
        console.warn("🛡️ Sentinel Safe Diagnostics - role document NOT found in client permissionRoles, checking fallback root roles...");
        const rootRoleRef = doc(db, 'roles', targetRole);
        getDoc(rootRoleRef).then((rootSnap) => {
          if (rootSnap.exists()) {
            console.log("🛡️ Sentinel Safe Diagnostics - fallback root role located:", rootSnap.data());
            rolePermissions.value = rootSnap.data().permissions || {};
          }
        }).catch(() => {});
      }
    }, (err) => {
      console.warn("🛡️ Silent fallback: Failed to sync role permissions from Sentinel Safe, using token claims:", err);
    });
  });

  onAuthStateChanged(auth, (user) => { fetchUser(user); });

  return {
    userProfile, clientProfile, claims, isAuthReady, isUserProfileLoading, clientId,
    isLoggedIn, role, isAdmin, isManager, isDirector, isHSManager, hasAdminAccess, permissions,
    mfaVerified, isMfaEnrolled, signatureSeal, isOnLeave,
    triggerSmsMfaHandshake, verifySmsMfaHandshake, triggerEmailMfaFallback, updateAbsenceStatus,
    dispatchPasswordResetSms, dispatchPasswordResetHandshake, completePasswordResetHandshake,
    isMaintenanceMode, isClientClosed, 
    unreadNotificationCount, pendingTalks, globalPendingActionCount, dismissedAlertIds,
    fetchUser, clearUserData, removeNotification, setGlobalPendingActionCount,
    deleteAllNotifications, purgeNotificationByRelatedId, dismissAlert
  };
});